import {
	createApi,
	fetchBaseQuery,
	retry,
} from '@reduxjs/toolkit/dist/query/react';

import { Mutex } from 'async-mutex';

import { CSRF_TOKEN_KEY } from '../constants';
import {
	sessionExpired,
	setAccessToken,
	setIsRefreshing,
} from '../features/authSlice';
import { refreshAccessTokenFromSession } from '../services/accessTokenRefresh';
import { platformElevationHeader } from '../services/elevationLease';
import { hasSessionHint } from '../services/sessionPresence';
import {
	clearLegacyAccessTokenCookie,
	clearLegacyRefreshTokenCookie,
} from '../services/token';
import { getCookie, isPublicPage } from '../utils/helper';

import type { RootState } from '../store';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type {
	BaseQueryFn,
	FetchArgs,
	FetchBaseQueryError,
	FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query/react';

const NO_RETRY_STATUS_CODES = [401, 403, 404];
const NO_RETRY_HTTP_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
const SESSION_EXPIRED_MESSAGE = 'Session expired. Please log in again.';

type StateShape = {
	tenantConfigState?: {
		api_url?: string;
	};
	authState?: {
		accessToken?: string;
		isRefreshing?: boolean;
	};
	api: {
		queries?: Record<string, any>;
	};
};

// Single mutex serializes refresh attempts across all 7 RTK Query slices that
// share this baseQuery. The previous `let refreshPromise` + `state.isRefreshing`
// dedupe was racy: Redux dispatches don't settle inside the same microtask, so
// N concurrent 401s on first load each read `isRefreshing === false`, each
// called `refreshAccessToken`, and only the last writer's promise was awaited
// by newcomers. The mutex is the canonical fix (per Redux Toolkit docs).
const refreshMutex = new Mutex();

const baseQuery =
	(
		baseQueryArgs = {},
	): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =>
	async (fetchArgs, api, extraOptions: any = {}) => {
		const { getState } = api;
		const state = getState() as RootState;
		const access_token = state.authState?.accessToken ?? '';

		const baseUrl =
			extraOptions.customBaseUrl ||
			state.tenantConfigState?.api_url ||
			process.env.REACT_APP_API_URL;

		if (!baseUrl) {
			console.error('No base URL available');
			return {
				error: { status: 'CUSTOM_ERROR' } as FetchBaseQueryError,
				data: undefined,
				meta: {},
			};
		}

		const csrfToken = getCookie(CSRF_TOKEN_KEY);

		return fetchBaseQuery({
			baseUrl,
			credentials: 'include',
			prepareHeaders: (headers: Headers) => {
				if (access_token && !headers.has('authorization')) {
					headers.set('authorization', `Bearer ${access_token}`);
				}
				if (csrfToken) {
					headers.set('X-CSRF-Token', csrfToken);
				}
				// Platform elevation lease (MBB-642): a session credential in the
				// same category as the access token, so it rides every authenticating
				// request from the shell — including the session query, which is how an
				// elevated session resolves as super-admin after Assume. Owner-bound
				// (only the lease's minter sends it) so a tab that outlives a logout
				// can't hand the next user a session they never assumed.
				for (const [name, value] of Object.entries(
					platformElevationHeader(state.authState?.user?.id),
				)) {
					headers.set(name, value);
				}
				return headers;
			},
			...baseQueryArgs,
		})(fetchArgs, api, extraOptions);
	};

const baseQueryWithRetry = retry(baseQuery(), {
	retryCondition: (
		error: FetchBaseQueryError,
		args: string | FetchArgs,
		{ attempt },
	) => {
		if (
			typeof error?.status === 'number' &&
			NO_RETRY_STATUS_CODES.includes(error.status)
		) {
			return false;
		}

		if (
			typeof args !== 'string' &&
			NO_RETRY_HTTP_METHODS.includes(args.method as string)
		) {
			return false;
		}

		return attempt <= 3;
	},
});
const baseQueryWithReauth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	let result = {} as QueryReturnValue<
		unknown,
		FetchBaseQueryError,
		FetchBaseQueryMeta
	>;

	try {
		if (args) {
			result = (await baseQueryWithRetry(
				args,
				api,
				extraOptions,
			)) as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
		}

		// A legal-acceptance 403 is a policy denial on a perfectly valid JWT.
		// Without this exemption every gated request becomes request → 403 →
		// /refresh-token → retry → 403, one token rotation apiece, serialized
		// through the global mutex — a refresh cannot fix it.
		const isLegalAcceptanceDenial =
			result?.error?.status === 403 &&
			(result.error.data as { code?: string } | undefined)?.code ===
				'legal_acceptance_required';

		if (
			!isLegalAcceptanceDenial &&
			(result?.error?.status === 401 || result?.error?.status === 403)
		) {
			if (refreshMutex.isLocked()) {
				// Another 401 is already refreshing. Wait for it; if it
				// produced a token, retry once with the new token.
				await refreshMutex.waitForUnlock();
				const state = api.getState() as StateShape;
				if (state.authState?.accessToken) {
					return baseQuery()(args, api, extraOptions);
				}
				return result;
			}

			const release = await refreshMutex.acquire();
			try {
				api.dispatch(setIsRefreshing(true));
				const newToken = await refreshAccessToken(api);
				if (newToken) {
					return baseQuery()(args, api, extraOptions);
				}
			} finally {
				api.dispatch(setIsRefreshing(false));
				release();
			}
		}
	} catch (error) {
		console.error('Base.Query.Error', error);
		result.error = {
			status: 'CUSTOM_ERROR',
			error: 'Unknown error occurred',
		};
	}

	return result;
};

const getLocalAnalyticsBaseUrl = (): string | undefined => {
	if (process.env.REACT_APP_PULSE_API_URL) {
		return process.env.REACT_APP_PULSE_API_URL;
	}

	if (
		typeof window !== 'undefined' &&
		['localhost', '127.0.0.1'].includes(window.location.hostname)
	) {
		return 'http://localhost:5008';
	}

	return undefined;
};

const getWorkflowApiBaseUrl = (): string => {
	if (process.env.REACT_APP_WORKFLOW_API_URL) {
		return process.env.REACT_APP_WORKFLOW_API_URL;
	}

	if (
		typeof window !== 'undefined' &&
		['localhost', '127.0.0.1'].includes(window.location.hostname)
	) {
		return 'http://localhost:5007/api';
	}

	return '/workflow/api';
};

const getBillingCustomerApiBaseUrl = (): string => {
	if (process.env.REACT_APP_BILLING_SERVICE_URL) {
		return process.env.REACT_APP_BILLING_SERVICE_URL;
	}

	if (
		typeof window !== 'undefined' &&
		['localhost', '127.0.0.1'].includes(window.location.hostname)
	) {
		return 'http://localhost:5013';
	}

	return '/billing';
};

const isLocalHost = (hostname: string): boolean =>
	['localhost', '127.0.0.1', '::1'].includes(hostname);

const LOCAL_WORKFLOW_SERVICE_PORTS = ['5007', '8000'];
const LOCAL_AUTH_SERVICE_PORT = '58080';

const getAccessApiBaseUrl = (): string => {
	const configured =
		process.env.REACT_APP_AUTH_SERVICE_URL || process.env.REACT_APP_MB_AUTH_URL;
	if (configured) {
		return configured.replace(/\/+$/, '');
	}

	const workflowBaseUrl = getWorkflowApiBaseUrl();
	try {
		const origin =
			typeof window !== 'undefined'
				? window.location.origin
				: 'http://localhost';
		const parsed = new URL(workflowBaseUrl, origin);
		const pathname = parsed.pathname.replace(/\/+$/, '');
		if (
			isLocalHost(parsed.hostname) &&
			LOCAL_WORKFLOW_SERVICE_PORTS.includes(parsed.port)
		) {
			parsed.port = LOCAL_AUTH_SERVICE_PORT;
			parsed.pathname = '/auth';
		} else if (pathname.endsWith('/workflow/api')) {
			parsed.pathname = `${pathname.slice(0, -'/workflow/api'.length)}/auth`;
		} else if (pathname.endsWith('/workflow')) {
			parsed.pathname = `${pathname.slice(0, -'/workflow'.length)}/auth`;
		} else if (pathname.endsWith('/api')) {
			parsed.pathname = `${pathname.slice(0, -'/api'.length)}/auth`;
		} else {
			parsed.pathname = '/auth';
		}
		parsed.search = '';
		parsed.hash = '';
		return parsed.toString().replace(/\/+$/, '');
	} catch (error) {
		console.debug('AccessApi.BaseUrl.Resolve.Error', error);
		return '/auth';
	}
};

// Separate refresh token logic
const refreshAccessToken = async (api: any): Promise<string | null> => {
	const state = api.getState() as StateShape;
	const apiUrl =
		state.tenantConfigState?.api_url || process.env.REACT_APP_API_URL;

	try {
		const accessToken = await refreshAccessTokenFromSession({
			apiUrl,
		});

		if (!accessToken) {
			throw new Error('Refresh token failed');
		}

		clearLegacyAccessTokenCookie();
		api.dispatch(setAccessToken(accessToken));
		return accessToken;
	} catch (error) {
		console.debug('Base.TokenRefresh.Error', error);
		// Only surface "Session expired" when we actually believed a session
		// existed. A visitor who lands on /signin with no cookie is not
		// "expired" — they were never logged in. Without this distinction
		// every fresh visit to the auth screen produces a spurious toast.
		const notice = hasSessionHint() ? SESSION_EXPIRED_MESSAGE : '';
		await handleAuthTokenError(api, notice);
		return null;
	}
};

// Dispatches the session-end event. The router observer (mounted in the web-app
// root) does the soft navigation; the auth screen reads the queued notice from
// the store. NO localStorage round-trip, NO `window.location.href` hard reload
// — those were the root cause of the "Session expired" toast persisting across
// screens.
const handleAuthTokenError = async (
	api: { dispatch: (action: unknown) => unknown },
	message: string,
) => {
	const currentPath = window.location.pathname;
	clearLegacyAccessTokenCookie();
	clearLegacyRefreshTokenCookie();
	// Suppress the redundant dispatch when we're already on a public page AND
	// have no message to surface — there's nothing for the observer to do.
	if (isPublicPage(currentPath) && !message) return;
	api.dispatch(sessionExpired({ notice: message }));
};

const api = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	endpoints: () => ({}),
	tagTypes: [
		'Me',
		'Journals',
		'Journal',
		'JournalCategories',
		'Integrations',
		'Enterprise',
		'EnterpriseUsers',
		'Autopilots',
		'UserPreferences',
		'UserRecentEvents',
		'Search',
		'Favorites',
		'EnterprisePreferences',
	],
});

const agentApi = createApi({
	reducerPath: 'agentApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: `${process.env.REACT_APP_AGENT_SERVICE_API_URL}`,
		}),
	endpoints: () => ({}),
	tagTypes: [
		'Agent',
		'ExecuteSearch',
		'RelatedPeople',
		'PeopleList',
		'Person',
		'PersonNotes',
		'PersonDeals',
		'PersonTasks',
		'PersonSharedUsers',
		'Company',
		'CompanyList',
		'Deal',
		'DealList',
		'Search',
		'NLSearch',
		'Draw',
		'DrawUserInteractions',
	],
});

const meetingApi = createApi({
	reducerPath: 'meetingApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: `${process.env.REACT_APP_MEETING_API_URL}`,
		}),
	endpoints: () => ({}),
	tagTypes: ['SharedUsers'],
});

const analyticsApi = createApi({
	reducerPath: 'analyticsApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: getLocalAnalyticsBaseUrl(),
		}),
	endpoints: () => ({}),
	tagTypes: [
		'BillingExport',
		'BillingAgents',
		'BillingFilters',
		'BillingDashboard',
	],
});

const workflowApi = createApi({
	reducerPath: 'workflowApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: getWorkflowApiBaseUrl(),
		}),
	endpoints: () => ({}),
	tagTypes: ['WorkflowAccessManagement', 'TranslationKey'],
});

const billingCustomerApi = createApi({
	reducerPath: 'billingCustomerApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: getBillingCustomerApiBaseUrl(),
		}),
	endpoints: () => ({}),
	tagTypes: ['BillingAccount', 'BillingTopUps', 'PaymentMethods'],
});

const accessApi = createApi({
	reducerPath: 'accessApi',
	baseQuery: (args, api, extraOptions) =>
		baseQueryWithReauth(args, api, {
			...extraOptions,
			customBaseUrl: getAccessApiBaseUrl(),
		}),
	endpoints: () => ({}),
	tagTypes: ['WorkflowAccessManagement'],
});

export const __testing = {
	getAccessApiBaseUrl,
	getBillingCustomerApiBaseUrl,
	getWorkflowApiBaseUrl,
};

export {
	api,
	agentApi,
	meetingApi,
	analyticsApi,
	workflowApi,
	billingCustomerApi,
	accessApi,
};
